import { HTMLInputTypeAttribute, KeyboardEvent } from 'react';
import { FreshSurveyDebugType } from 'freemium-survey-components';
import { omit, uniq } from 'lodash';
import { useLocation } from 'react-router-dom';
import {
  AccountInfoType,
  AccountUserType,
  AccountUsersResponseType,
  AllUsersType,
  CollectorChannelType,
  ColumnSettingsType,
  ContactListType,
  FieldChoicesType,
  FormServFieldType,
  OptionType,
  RuleType,
  SurveyBlockType,
  SurveyType,
  TimeZone,
  UserType,
  WorkflowActionDelayTypes,
} from './types';
import {
  COLUMNS_HIDE_ORDERING_KEY,
  DEFAULT_TIME_FORMAT,
  FRESH_SURVEY_DEBUG,
  GLOBAL_DATE_TIME_FORMAT_12_HRS,
  GLOBAL_DATE_TIME_FORMAT_24_HRS,
  GLOBAL_SURVEY_LIMIT_RESET_TIME_FORMAT_12,
  GLOBAL_SURVEY_LIMIT_RESET_TIME_FORMAT_24,
  HEX_COLOR_REGEX,
  timeZones,
} from './constants';

export const isValidDateString: (value: string) => boolean = (value) =>
  Boolean(value) && new Date(value).toString() !== 'Invalid Date';

export const getAllUsersFromResponse: (
  usersResponse?: AccountUsersResponseType,
  key?: 'display_id' | 'freshid_user_id',
) => AllUsersType = (usersResponse, key = 'freshid_user_id') => {
  const allUsers: AllUsersType = {};
  const { account_users = [] } = usersResponse || {};
  account_users.forEach((account_user) => {
    if (account_user.user && account_user.user[key]) {
      allUsers[account_user.user[key] || ''] = account_user.user;
    }
  });
  return allUsers;
};

export const timeFormats: OptionType[] = [
  { id: '12', label: 'settings.account_info.time_format.12' },
  { id: '24', label: 'settings.account_info.time_format.24' },
];

export const getAccountTimeFormat = (accountInfo?: AccountInfoType) =>
  !accountInfo?.time_format || accountInfo?.time_format === DEFAULT_TIME_FORMAT
    ? GLOBAL_DATE_TIME_FORMAT_12_HRS
    : GLOBAL_DATE_TIME_FORMAT_24_HRS;

export const getAccountTimeFormatForGlobalSurveyLimit = (
  accountInfo?: AccountInfoType,
) =>
  !accountInfo?.time_format || accountInfo?.time_format === DEFAULT_TIME_FORMAT
    ? GLOBAL_SURVEY_LIMIT_RESET_TIME_FORMAT_12
    : GLOBAL_SURVEY_LIMIT_RESET_TIME_FORMAT_24;

export const getRulePayload: (rule: RuleType) => RuleType = (rule) => {
  const modifiedRule: RuleType = rule || {};
  // To-Do disable once backend accepts id inside condition
  if (
    modifiedRule &&
    modifiedRule.condition_sets &&
    modifiedRule.condition_sets.length > 0
  ) {
    modifiedRule.condition_sets = modifiedRule.condition_sets.map(
      (condition_set) =>
        omit(
          {
            ...condition_set,
            conditions: (condition_set.conditions || []).map((condition) =>
              omit(condition, ['id']),
            ),
          },
          ['id'],
        ),
    );
  }
  return omit(modifiedRule, ['version']);
};

export const getChoices = ({
  field,
  contactLists = [],
}: {
  field: FormServFieldType;
  contactLists: ContactListType[];
}): FieldChoicesType[] => {
  const { name } = field;
  switch (name) {
    case 'list_ids':
      return contactLists.map(({ display_id, label }: ContactListType) => ({
        id: display_id || '',
        value: label || '',
      }));
    case 'timezone':
      return timeZones.map(({ offset, timeZone }: TimeZone) => ({
        id: timeZone || '',
        value: `${timeZone} (${offset})`,
      }));
    default:
      return field.choices || [];
  }
};

export const getFilterValueTypeFromFieldType: (
  fieldType: number,
) => HTMLInputTypeAttribute = (fieldType) => {
  switch (fieldType) {
    case 1:
    case 3:
    case 4:
    case 10:
      return 'text';
    case 8:
    case 13:
    case 20:
      return 'number';
    case 17:
      return 'date';
    case 7:
      return 'datetime-local';
    default:
      return 'text';
  }
};

export const getHtmlTypeFromFieldType: (
  fieldType: number,
) => HTMLInputTypeAttribute = (fieldType) => {
  switch (fieldType) {
    case 1:
      // case 16:
      return 'text';
    case 3:
      return 'email';
    case 4:
      return 'tel';
    case 5:
      return 'checkbox';
    case 7:
      // case 19:
      return 'datetime-local';
    case 17:
      return 'date';
    case 8:
    case 20:
    case 13:
      return 'number';
    case 10:
      return 'url';
    // case 11:
    //   return 'file';
    case 12:
      return 'radio';
    default:
      return 'text';
  }
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// convert hours and days to minutes
export const convertDaysAndHrsToMins = (delay: any, delayType: any) => {
  const value = Number(delay);
  if (delayType?.label === 'Days') {
    return value * 24 * 60;
  }
  if (delayType?.label === 'Hours') {
    return value * 60;
  }
  return value;
};

// convert minutes to days or hours
export const convertMinsToDaysOrHrs = (
  delay: number,
  delayType: WorkflowActionDelayTypes,
) => {
  const value = Number(delay);
  if (delayType === 'Days') {
    return value / 1440;
  }
  if (delayType === 'Hours') {
    return value / 60;
  }
  return value;
};

// convert string with tags or plain string or html or anything to Quill HTML
export const convertStringWithTagsToQuillHTML = (
  stringWithTags: string,
): string => {
  const element = document.createElement('div');
  element.innerHTML = stringWithTags;
  const isPlainString = element.children.length === 0;
  const modifiedStringWithTags = convertPlaceholderSpanToTags(stringWithTags);
  const modifiedQuillHtml = convertTagsToPlaceholderSpan(
    modifiedStringWithTags,
  );
  const quillHtml = isPlainString
    ? `<p>${modifiedQuillHtml}</p>`
    : modifiedQuillHtml.replace(/<p style="white-space:pre-wrap">/g, '<p>');
  return quillHtml.replace(/[\u200B-\u200D\uFEFF]/g, '');
};

// convert Quill HTML to string
export const convertQuillHTMLToText = (
  quillHtml: string,
  preserveNewLine = false,
): string => {
  const htmlContainer = document.createElement('div');
  if (preserveNewLine)
    htmlContainer.innerHTML = quillHtml.replace(/<\/p>/g, '</p>\n');
  else {
    htmlContainer.innerHTML = quillHtml;
  }
  const textValue = (
    htmlContainer.textContent ||
    htmlContainer.innerText ||
    ''
  ).replace(/[\u200B-\u200D\uFEFF]/g, '');
  return textValue;
};

// convert Quill HTML to sanitized HTML
export const convertQuillHTMLToSanitizedHTML = (html: string) => {
  const htmlContainer = document.createElement('div');
  htmlContainer.innerHTML = html;

  // 1. remove comments and whitespace-only #text nodes
  const cleanHTML = (node: any) => {
    for (let n = 0; n < node.childNodes.length; n += 1) {
      const child = node.childNodes[n];
      if (
        child.nodeType === 8
        // || (child.nodeType === 3 && !/\S/.test(child.nodeValue))
      ) {
        node.removeChild(child);
        n -= 1;
      } else if (child.nodeType === 1) {
        cleanHTML(child);
      }
    }
  };
  cleanHTML(htmlContainer);

  // 2. replace all placeholder-tag elements
  const placeholderTags =
    htmlContainer.getElementsByClassName('placeholder-tag');
  while (placeholderTags.length > 0) {
    const tempNode = document.createElement('tempnode');
    tempNode.textContent = placeholderTags[0].textContent;

    const parentNode = placeholderTags[0].parentNode;
    parentNode && parentNode.replaceChild(tempNode, placeholderTags[0]);
  }

  // 3. remove BOM from from html string
  let sanitized_html = htmlContainer.innerHTML;
  sanitized_html = sanitized_html.replace(/\uFEFF/g, '');

  // 4. remove temp nodes from html string
  sanitized_html = sanitized_html
    .replace(/<tempnode>/g, '')
    .replace(/<\/tempnode>/g, '');

  return sanitized_html;
};

export const isElementInView = (element: Element) => {
  if (!element || (!window && !document)) return false;
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const getDefaultColumnOrder = ({
  columnSettings,
  fields,
}: {
  fields: FormServFieldType[];
  columnSettings: ColumnSettingsType;
}): string[] => {
  const disabledFields = fields.filter(
    (field: FormServFieldType) =>
      field?.name && columnSettings?.[field.name]?.disableReordering,
  );
  const allFields = [...disabledFields, ...fields];
  return uniq(allFields.map((allField) => allField[COLUMNS_HIDE_ORDERING_KEY]));
};

/* eslint-disable */
export const validURL = (str: string) => {
  const pattern = new RegExp(
    /^((https?|ftp):)?\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/,
    'i',
  );

  return pattern.test(str);
};

export const heapInit = (
  loggedInUser?: UserType,
  currentAccountUser?: AccountUserType,
  accountInfo?: AccountInfoType,
) => {
  if (window && window.heap) {
    const heap = window.heap;
    heap.identify(loggedInUser?.display_id);

    heap.addUserProperties({
      email: loggedInUser?.email,
      first_name: loggedInUser?.first_name,
      last_name: loggedInUser?.last_name,
      user_locale: loggedInUser?.locale,
      user_status: loggedInUser?.status,
      user_active: loggedInUser?.active,
      user_id: loggedInUser?.display_id,
      job_title: loggedInUser?.job_title,
      role: currentAccountUser?.user_role,
      account_id: accountInfo?.id,
      account_name: accountInfo?.name,
      account_domain: accountInfo?.domain,
      organisation_domain: accountInfo?.organisation_domain,
      account_timezone: accountInfo?.timezone,
      account_region_code: accountInfo?.region_code,
      account_locale: accountInfo?.locale,
      account_created_by: accountInfo?.created_by_id,
      account_created_at: accountInfo?.created_at,
      account_active: accountInfo?.active,
      account_status: accountInfo?.status,
      dkim_domain: accountInfo?.dkim_domain,
      dkim_verified: accountInfo?.dkim_verified,
      custom_domain: accountInfo?.custom_domain,
      custom_domain_status: accountInfo?.custom_domain_status,
      spam_score: accountInfo?.spam_score,
    });
  }
};

export const beamerInit = (loggedInUser?: UserType) => {
  // beamer

  if (!document.getElementById('beamer_script')) {
    window.beamer_config = {
      product_id: 'oVRmCWDR41515',
      selector: '#beamer-mount',
      lazy: true,
    };

    var body = document.getElementsByTagName('body')[0];
    var js = document.createElement('script');
    js.type = 'text/javascript';
    js.id = 'beamer_script';
    js.defer = true;
    js.src = 'https://app.getbeamer.com/js/beamer-embed.js';

    if (!document.querySelector(`[src='${js.src}']`)) {
      body.appendChild(js);
    }
  } else {
    console.log('loading beamer');
    if (window && window.beamer_config) {
      window.beamer_config.user_firstname = loggedInUser?.first_name;
      window.beamer_config.user_lastname = loggedInUser?.last_name;
      window.beamer_config.user_email = loggedInUser?.email;
      window.beamer_config.user_id = loggedInUser?.display_id;
      if (window.Beamer) window.Beamer.init();
    }
  }
};

export const getI18nLangCode = (langCode: string | null | undefined) => {
  if (!langCode) return 'en';
  const langCodeArr = langCode.split('-');
  if (langCodeArr.length > 1) {
    langCodeArr[1] = langCodeArr[1].toUpperCase();
  }
  return langCodeArr.join('-');
};

export const convertPlaceholderSpanToTags = (
  quillHtmlString: string,
): string => {
  return quillHtmlString
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(
      /<span class="placeholder-tag"><span contenteditable="false">(.*?)<\/span><\/span>/g,
      '$1',
    );
};

export const convertTagsToPlaceholderSpan = (
  quillHtmlString: string,
): string => {
  return quillHtmlString.replace(
    /\{\{(.*?)\}\}/g,
    (val) =>
      `<span class="placeholder-tag"><span contenteditable="false">{{${val.substring(
        2,
        val.length - 2,
      )}}}</span></span>`,
  );
};

const removeUnwantedTags = (element: HTMLDivElement) => {
  for (let i = 0; i < element.children.length; i += 1) {
    const child = element.children[i];
    if (child.children.length > 0) {
      removeUnwantedTags(child as HTMLDivElement);
    }
    // &nbsp; inside a <p></p> tag should be replaced with </br> tag
    if (child.textContent?.trim() === '') {
      const brElement = document.createElement('br');
      child.replaceWith(brElement);
    }
    // Line break should have </br> tag instead of a new <p></p> tag
    if (child && child.tagName === 'BR') {
      let parent = child.parentElement;
      if (parent?.children.length === 1) {
        parent.replaceWith(child);
      }
    }
  }
  return element;
};

// 1. bold
// 2. italic
// 3. underline
// 4. bullet
// 5. align left
// 6. align center
// 7. align right
// 8. link
// Only above 8 formats are supported
export const getPayloadQuestionText = (quillHtml: string): string => {
  const htmlWithPlaceholderSpanRemoved =
    convertPlaceholderSpanToTags(quillHtml);
  const element = document.createElement('div');
  element.innerHTML = htmlWithPlaceholderSpanRemoved;
  removeUnwantedTags(element);
  return element.innerHTML;
};

export const getChannelName = (channelType: CollectorChannelType): string => {
  switch (channelType) {
    case 'EMAIL':
      return 'channel.email';
    case 'EMAIL_EMBED':
      return 'channel.email_embed';
    case 'WEB_APP':
      return 'channel.web_app';
    case 'WEB_LINK':
      return 'channel.shareable_link';
  }
  return '';
};

export const getQuestionNamesFromBlock = (block?: SurveyBlockType) => {
  if (!block) return [];

  if (block.category?.length)
    return block.category
      .sort((a, b) => Number(b.id) - Number(a.id))
      .map(({ question_name }) => question_name)
      .filter(Boolean) as string[];

  if (block.question_name) return [block.question_name];

  return [];
};

export const getSurveyFormValues = ({
  survey,
}: {
  survey: SurveyType;
}): SurveyType => {
  if (!survey.blocks) return survey;
  const blockAndQuestions: Record<string, string[]> = {};
  survey?.blocks?.forEach((b) => {
    if (b.name) {
      blockAndQuestions[b.name] = getQuestionNamesFromBlock(b);
    }
  });
  const blocks: SurveyBlockType[] | null | undefined = survey.blocks?.filter(
    (b) => {
      if (!b.is_deleted) return true;
      const otherQuestionNames: string[] = [];
      Object.keys(blockAndQuestions).forEach((blockName) => {
        if (blockName !== b.name) {
          otherQuestionNames.push(...blockAndQuestions[blockName]);
        }
      });
      const currentBlockQuestionNames = getQuestionNamesFromBlock(b);
      if (
        currentBlockQuestionNames.every((cbqn) =>
          otherQuestionNames.includes(cbqn),
        )
      ) {
        return false;
      }
      return true;
    },
  );

  const data: SurveyType = {
    ...survey,
    blocks,
  };
  return data;
};

export const isValidColor = (color?: string | null) => {
  if (!color) {
    return false;
  }

  if (color.startsWith('#')) {
    return HEX_COLOR_REGEX.test(color);
  }

  const s = new Option().style;
  s.color = color;
  return !!s.color;
};

export const colorNameToHex = (colorString: string) => {
  const ctx = document.createElement('canvas').getContext('2d');
  let hexColor = colorString;
  if (ctx) {
    ctx.fillStyle = colorString;

    if (ctx.fillStyle === '#000000' && colorString !== 'black') {
      hexColor = colorString;
    } else {
      hexColor = ctx.fillStyle;
    }
  }

  return hexColor;
};

export const handleKeyboardEnter =
  <T = Element>(callback: (e?: KeyboardEvent<T>) => void) =>
  (e: KeyboardEvent<T>) => {
    if (e.key === 'Enter') {
      callback(e);
    }
  };

export const useQueryParams = () => new URLSearchParams(useLocation().search);

export const getFreshSurveyDebug = (): FreshSurveyDebugType['fwa:debug'] => {
  if (
    typeof window === 'undefined' ||
    !window ||
    typeof localStorage === 'undefined' ||
    !localStorage
  )
    return {};
  const fsDebugJson = localStorage.getItem(FRESH_SURVEY_DEBUG);
  const fsDebug = JSON.parse(
    fsDebugJson ?? '{}',
  ) as FreshSurveyDebugType['fwa:debug'];
  return fsDebug;
};

const fsDebug = getFreshSurveyDebug();

export const appLogger = (title: string, props?: any) => {
  if (fsDebug.log) {
    console.log(`${FRESH_SURVEY_DEBUG}:${title}`, props);
  }
};
